import React, { useEffect, useRef, useState, createContext } from 'react';
import { API } from 'aws-amplify';
import { useAuth } from './Auth';
import { Pairing } from '../models/PairingInterface';
import { getMonth, getYear } from '../helpers/dateHelpers';
import { SearchStateInterface } from '../models/SearchInterface';
import { useApiError } from './ApiErrorProvider';
import { error } from '../constants/bindings';

const pairingDefault = { pairingList: [] };
const PairingContext = createContext({
  loading: true,
  pairingPackageData: pairingDefault as Pairing,
  getPairingPackageData: (search: SearchStateInterface) => { },
});


function waitForTime(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}


export const PairingProvider = (props) => {
  const { user } = useAuth();
  const {setErrorCode} = useApiError();
  const [loading, setLoading] = useState(true);
  const [pairingPackageData, setPairingPackageData] = useState<Pairing>(pairingDefault);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const getPairingPackageData = async (search) => {
    if (!user || !search) {
      return;
    }
    const MAX_RETRY = 3;

    async function retryWithTimeout(count) {
      if (!mounted.current) {
        return;
      }
      try {
        setLoading(true);
        if(count > 0) {
          const timeToWait = 5000;
          await waitForTime(timeToWait);
        }
        if (!mounted.current) {
          return;
        }
        const requestObject = {
          body: {
            year: getYear(search.selectedDate),
            month: getMonth(search.selectedDate),
            "baseAirport": search.selectedBase,
            "position": search.selectedPosition,
            "equipment": search.selectedEquipment
          }
        };
        if (search.selectedBase?.toLowerCase() === 'all') {
          delete requestObject.body.baseAirport;
        }
        if (search.selectedPosition?.toLowerCase() === 'all') {
          delete requestObject.body.position;
        }
        if (search.selectedEquipment?.toLowerCase() === 'all') {
          delete requestObject.body.equipment;
        }
        const pairingPackage = await API.post('crewlife', '/pairings/general', requestObject);
        if (!mounted.current) {
          return;
        }
        const pairinglistWithId = (pairingPackage?.pairingList ?? []).map((row, index) => ({ ...row, id: index + 1 }));
        setPairingPackageData({ ...pairingPackage, pairingList: pairinglistWithId });
        setLoading(false); 
      } catch (e:any) {
        if (!mounted.current) {
          return;
        }
        setLoading(false);
        if(count < MAX_RETRY-1) retryWithTimeout(count + 1)
        else if( e.response?.status) { setErrorCode(e.response?.status); }
        else { setErrorCode(error.DEFAULT); }
      }
    }
    retryWithTimeout(0);
  };


  return (
    <PairingContext.Provider
      value={{
        loading,
        pairingPackageData,
        getPairingPackageData,
      }}>
      {props.children}
    </PairingContext.Provider>
  );
};

export const usePairingPackage = () => {
  return React.useContext(PairingContext);
};

